/**
 * useCancelFeesState — les deux sorties de l'étape 3, et la résiliation sans frais.
 *
 * Port littéral de `CancelFeesScreen.js:138-191` : le RDV Calendly avec le motif pré-rempli, et
 * « Poursuivre la résiliation » qui, selon le montant, pousse l'étape 4 ou pose la question et
 * poste tout de suite `cancelSubscription` avec `fees_displayed_cents: 0`.
 *
 * Le POST lui-même est celui de l'étape 4 (`useCancelSubscriptionSubmit`) : ici le montant affiché
 * est nul, et l'accusé porte en plus ce que l'aperçu sait du jalon et de la reprise.
 */
import { useCallback, useMemo, useReducer } from "react"
import { useNavigation } from "@react-navigation/native"

import { openCalendly } from "../../../../../../data/utility/Utils"
import {
    cancelCalendlyLabel,
    cancelFeesCredit,
    cancelFeesConfirmMessage,
    toUICancelPaymentOutcome,
    type ApiCancellationFees,
} from "../../../../adapters/parent/SubscriptionCancel/subscriptionCancelAdapter"
import { nextScreenAfterFees, type CancelFlowNavigation, type CancelFlowRouteParams } from "./cancelFlowNavigation"
import { useCancelSubscriptionSubmit } from "./useCancelSubscriptionSubmit"

type State = {
    confirmVisible: boolean
    submitting: boolean
    /** Le refus du prélèvement n'existe pas ici (frais nuls) : seul un échec serveur remonte. */
    feedback: string | null
}

type Action =
    | { type: "OPEN_CONFIRM" }
    | { type: "CLOSE_CONFIRM" }
    | { type: "SUBMIT" }
    | { type: "FAILED"; message: string }
    | { type: "CLOSE_FEEDBACK" }

const initialState: State = { confirmVisible: false, submitting: false, feedback: null }

function reducer(state: State, action: Action): State {
    switch (action.type) {
        case "OPEN_CONFIRM":
            return { ...state, confirmVisible: true }
        case "CLOSE_CONFIRM":
            return { ...state, confirmVisible: false }
        case "SUBMIT":
            return { confirmVisible: false, submitting: true, feedback: null }
        case "FAILED":
            return { ...state, submitting: false, feedback: action.message }
        case "CLOSE_FEEDBACK":
            return { ...state, feedback: null }
        default:
            return state
    }
}

export type UseCancelFeesStateArgs = {
    routeParams: CancelFlowRouteParams
    preview: ApiCancellationFees | null
    hasFees: boolean
    /** Le blob `currentUser` que Calendly pré-remplit — vient du view-model. */
    calendlySubject: unknown
}

export function useCancelFeesState(args: UseCancelFeesStateArgs) {
    const { routeParams, preview, hasFees, calendlySubject } = args
    const navigation = useNavigation<CancelFlowNavigation>()
    const [state, dispatchUi] = useReducer(reducer, initialState)

    const submitCancel = useCancelSubscriptionSubmit({
        routeParams,
        // Étape 3 : on n'arrive à la question que s'il n'y a RIEN à prélever.
        dueCents: 0,
        endOfMonth: preview?.end_of_month ?? null,
        parentEmail: preview?.parent_email ?? null,
        goodbyeExtras: {
            milestone_cancellation: !!preview?.milestone_cancellation,
            resume_date: preview?.resume_date ?? null,
        },
        onError: (error) => dispatchUi({ type: "FAILED", message: toUICancelPaymentOutcome(error).notice.message }),
    })

    const onBookAppointment = useCallback(() => {
        openCalendly(calendlySubject, "cancel", cancelCalendlyLabel(routeParams.motif))
    }, [calendlySubject, routeParams.motif])

    const onContinue = useCallback(() => {
        if (hasFees) {
            navigation.push(nextScreenAfterFees(), {
                ...routeParams,
                fees_cents: preview?.fees_cents,
                credit_applied_cents: preview?.credit_applied_cents ?? 0,
                balance_due_cents: preview?.balance_due_cents ?? 0,
                amount_due_cents: cancelFeesCredit(preview).dueCents,
                end_of_month: preview?.end_of_month,
                commitment_ends_at: preview?.commitment_ends_at,
                remaining_months: preview?.remaining_months,
                default_pm: preview?.default_pm,
                parent_email: preview?.parent_email,
                milestone_cancellation: !!preview?.milestone_cancellation,
                resume_date: preview?.resume_date,
            })
            return
        }
        dispatchUi({ type: "OPEN_CONFIRM" })
    }, [hasFees, navigation, preview, routeParams])

    const onConfirm = useCallback(() => {
        dispatchUi({ type: "SUBMIT" })
        submitCancel()
    }, [submitCancel])

    const actions = useMemo(
        () => ({
            onBookAppointment,
            onContinue,
            onConfirm,
            onCancelConfirm: () => dispatchUi({ type: "CLOSE_CONFIRM" }),
            onCloseFeedback: () => dispatchUi({ type: "CLOSE_FEEDBACK" }),
            onBack: () => navigation.goBack(),
            onClose: () => navigation.popToTop(),
        }),
        [navigation, onBookAppointment, onContinue, onConfirm]
    )

    return {
        confirmVisible: state.confirmVisible,
        confirmMessage: cancelFeesConfirmMessage(preview),
        submitting: state.submitting,
        feedback: state.feedback,
        actions,
    }
}
